import React, { useState, useEffect, useCallback } from 'react';
import './ForwardMessage.css';

const API_BASE = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? 'https://intranet.nesimltd.co.ke' : 'http://localhost:5000');

const ForwardMessage = ({ 
  message, 
  token, 
  user, 
  onClose, 
  onForward 
}) => {
  const [rooms, setRooms] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedTargets, setSelectedTargets] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [forwarding, setForwarding] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('groups');

  // Fetch available rooms and users
  const fetchTargets = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch rooms
      const roomsResponse = await fetch(`${API_BASE}/api/chat/rooms`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      // Fetch users
      const usersResponse = await fetch(`${API_BASE}/api/chat/users`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (roomsResponse.ok && usersResponse.ok) {
        const roomsData = await roomsResponse.json();
        const usersData = await usersResponse.json();
        
        setRooms(roomsData || []);
        setUsers((usersData || []).filter(u => u._id !== (user._id || user.id)));
      } else {
        throw new Error('Failed to fetch forward targets');
      }
    } catch (err) {
      console.error('Error fetching forward targets:', err);
      setError('Failed to load rooms and users');
    } finally {
      setLoading(false);
    }
  }, [token, user]);

  useEffect(() => {
    fetchTargets();
  }, [fetchTargets]);

  // Helper: get display name for a DM room (other participant)
  const getDMDisplayName = (dmRoom) => {
    try {
      const currentUserId = user._id || user.id;
      const members = dmRoom.members || [];
      const other = members.find(m => (m?._id || m) !== currentUserId);
      return other?.name || other?.email || dmRoom.name || 'Direct Message';
    } catch (_) {
      return dmRoom.name || 'Direct Message';
    }
  };

  // Handle target selection
  const handleTargetToggle = (target, type) => {
    const targetId = `${type}:${target._id}`;
    setSelectedTargets(prev => {
      if (prev.some(t => t.id === targetId)) {
        return prev.filter(t => t.id !== targetId);
      } else {
        const computedName = type === 'room'
          ? (target.type === 'direct' ? getDMDisplayName(target) : target.name)
          : (target.name || target.email);
        return [...prev, {
          id: targetId,
          type,
          data: target,
          name: computedName
        }];
      }
    });
  };

  // Handle forward
  const handleForward = async () => {
    if (selectedTargets.length === 0) {
      setError('Please select at least one destination');
      return;
    }

    try {
      setForwarding(true);
      setError(null);

      const forwardPromises = selectedTargets.map(async (target) => {
        let targetRoomId;
        
        if (target.type === 'room') {
          targetRoomId = target.data._id;
        } else if (target.type === 'user') {
          // For user forwards, we need to create or find a DM room
          targetRoomId = await getOrCreateDMRoom(target.data._id);
        }

        // Create WhatsApp-style forwarded message (just the content)
        const forwardedContent = message.content;

        const forwardMeta = {
          isForwarded: true,
          originalSender: {
            _id: message.sender?._id,
            name: message.sender?.name
          },
          originalTimestamp: message.createdAt,
          forwardedBy: {
            _id: user._id || user.id,
            name: user.name
          },
          forwardedAt: new Date().toISOString()
        };

        console.log('📨 Forwarding message with meta:', forwardMeta);

        // Forward the message
        const response = await fetch(
          `${API_BASE}/api/chat/rooms/${targetRoomId}/messages`,
          {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              content: forwardedContent,
              messageType: 'text',
              attachments: message.attachments || [],
              client_msg_id: `forward_${Date.now()}_${Math.random()}`,
              meta: forwardMeta
            })
          }
        );

        if (!response.ok) {
          throw new Error(`Failed to forward to ${target.name}`);
        }

        return { target: target.name, success: true };
      });

      const results = await Promise.allSettled(forwardPromises);
      const successful = results.filter(r => r.status === 'fulfilled').length;
      const failed = results.filter(r => r.status === 'rejected').length;

      if (successful > 0) {
        onForward({
          message: `Message forwarded to ${successful} destination${successful > 1 ? 's' : ''}`,
          type: 'success'
        });
        onClose();
      }

      if (failed > 0) {
        setError(`Failed to forward to ${failed} destination${failed > 1 ? 's' : ''}`);
      }
    } catch (err) {
      console.error('Error forwarding message:', err);
      setError('Failed to forward message');
    } finally {
      setForwarding(false);
    }
  };

  // Get or create DM room with user
  const getOrCreateDMRoom = async (targetUserId) => {
    try {
      // Try to find existing DM room
      const existingDM = rooms.find(room => 
        room.type === 'direct' && 
        room.members?.some(member => (member._id || member) === targetUserId)
      );

      if (existingDM) {
        return existingDM._id;
      }

      // Create new DM room
      const response = await fetch(`${API_BASE}/api/chat/rooms`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: 'Direct Message',
          type: 'direct',
          isPrivate: true,
          members: [targetUserId]
        })
      });

      if (response.ok) {
        const data = await response.json();
        return data.room._id;
      } else {
        throw new Error('Failed to create DM room');
      }
    } catch (err) {
      console.error('Error getting/creating DM room:', err);
      throw err;
    }
  };

  // Partition rooms into groups and DM rooms
  const groupRooms = rooms.filter(r => r.type !== 'direct');
  const dmRooms = rooms.filter(r => r.type === 'direct');

  // Filter targets based on search
  const filteredGroups = groupRooms.filter(room =>
    room.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredDMRooms = dmRooms.filter(room => {
    const name = (room.name || '').toLowerCase();
    const dmName = getDMDisplayName(room).toLowerCase();
    const term = searchTerm.toLowerCase();
    return name.includes(term) || dmName.includes(term);
  });

  const filteredUsers = users.filter(user =>
    user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Get user initials for avatar
  const getUserInitials = (name) => {
    return name ? name.split(' ').map(n => n[0]).join('').toUpperCase() : '?';
  };

  return (
    <div className="forward-message-modal">
      <div className="forward-modal-overlay" onClick={onClose} />
      <div className="forward-modal-content">
        <div className="forward-header">
          <h3>Forward Message</h3>
          <button className="forward-close-btn" onClick={onClose}>×</button>
        </div>

        {/* Message Preview */}
        <div className="forward-message-preview">
          <div className="preview-header">
            <span className="preview-sender">{message.sender?.name || 'Unknown'}</span>
            <span className="preview-time">
              {new Date(message.createdAt).toLocaleString()}
            </span>
          </div>
          <div className="preview-content">
            {message.content}
            {message.attachments && message.attachments.length > 0 && (
              <div className="preview-attachments">
                📎 {message.attachments.length} attachment{message.attachments.length > 1 ? 's' : ''}
              </div>
            )}
          </div>
        </div>

        {/* Search */}
        <div className="forward-search">
          <input
            type="text"
            placeholder="Search rooms or users..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>

        {/* Instructions */}
        {selectedTargets.length === 0 && (
          <div className="forward-instructions">
            <span className="instruction-icon">👆</span>
            Select rooms or users to forward this message to
          </div>
        )}

        {/* Tabs */}
        <div className="forward-tabs">
          <button 
            className={`tab-btn ${activeTab === 'groups' ? 'active' : ''}`}
            onClick={() => setActiveTab('groups')}
          >
            Groups ({filteredGroups.length})
          </button>
          <button 
            className={`tab-btn ${activeTab === 'dms' ? 'active' : ''}`}
            onClick={() => setActiveTab('dms')}
          >
            DMs ({filteredDMRooms.length + filteredUsers.length})
          </button>
        </div>

        {/* Target List */}
        <div className="forward-targets">
          {loading ? (
            <div className="forward-loading">Loading...</div>
          ) : error ? (
            <div className="forward-error">
              {error}
              <button onClick={fetchTargets} className="retry-btn">Retry</button>
            </div>
          ) : (
            <div className="targets-list">
              {activeTab === 'groups' ? (
                filteredGroups.length === 0 ? (
                  <div className="no-targets">No groups found</div>
                ) : (
                  filteredGroups.map(room => (
                    <div
                      key={room._id}
                      className={`target-item ${selectedTargets.some(t => t.id === `room:${room._id}`) ? 'selected' : ''}`}
                      onClick={() => handleTargetToggle(room, 'room')}
                    >
                      <div className="target-avatar room-avatar">#</div>
                      <div className="target-info">
                        <div className="target-name">{room.name}</div>
                        <div className="target-meta">
                          {room.type === 'private' ? 'Private Group' : 'Group'} • {room.memberCount || 0} members
                        </div>
                      </div>
                      <div className="target-checkbox">
                        {selectedTargets.some(t => t.id === `room:${room._id}`) ? '✓' : ''}
                      </div>
                    </div>
                  ))
                )
              ) : (
                <>
                  {/* Existing DM conversations */}
                  {filteredDMRooms.length > 0 && (
                    <>
                      {filteredDMRooms.map(dm => (
                        <div
                          key={dm._id}
                          className={`target-item ${selectedTargets.some(t => t.id === `room:${dm._id}`) ? 'selected' : ''}`}
                          onClick={() => handleTargetToggle(dm, 'room')}
                        >
                          <div className="target-avatar user-avatar">{getUserInitials(getDMDisplayName(dm))}</div>
                          <div className="target-info">
                            <div className="target-name">{getDMDisplayName(dm)}</div>
                            <div className="target-meta">Direct Message</div>
                          </div>
                          <div className="target-checkbox">
                            {selectedTargets.some(t => t.id === `room:${dm._id}`) ? '✓' : ''}
                          </div>
                        </div>
                      ))}
                    </>
                  )}

                  {/* Start new DM with a contact */}
                  {filteredUsers.length === 0 ? (
                    <div className="no-targets">No contacts found</div>
                  ) : (
                    filteredUsers.map(user => (
                      <div
                        key={user._id}
                        className={`target-item ${selectedTargets.some(t => t.id === `user:${user._id}`) ? 'selected' : ''}`}
                        onClick={() => handleTargetToggle(user, 'user')}
                      >
                        <div className="target-avatar user-avatar">
                          {getUserInitials(user.name || user.email)}
                        </div>
                        <div className="target-info">
                          <div className="target-name">{user.name || user.email}</div>
                          <div className="target-meta">Contact</div>
                        </div>
                        <div className="target-checkbox">
                          {selectedTargets.some(t => t.id === `user:${user._id}`) ? '✓' : ''}
                        </div>
                      </div>
                    ))
                  )}
                </>
              )}
            </div>
          )}
        </div>

        {/* Selected Targets */}
        {selectedTargets.length > 0 && (
          <div className="selected-targets">
            <div className="selected-header">
              <span className="selected-icon">✅</span>
              Selected ({selectedTargets.length}) - Ready to forward!
            </div>
            <div className="selected-list">
              {selectedTargets.map(target => (
                <div key={target.id} className="selected-target">
                  <span className="target-type-icon">
                    {target.type === 'room' ? '🏠' : '👤'}
                  </span>
                  <span className="selected-name">{target.name}</span>
                  <button 
                    className="remove-target-btn"
                    onClick={() => setSelectedTargets(prev => prev.filter(t => t.id !== target.id))}
                    title="Remove from selection"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="forward-actions">
          <div className="action-info">
            {selectedTargets.length > 0 ? (
              <span className="selection-count">
                ✅ {selectedTargets.length} destination{selectedTargets.length > 1 ? 's' : ''} selected
              </span>
            ) : (
              <span className="selection-hint">
                💡 Click on rooms or users above to select them
              </span>
            )}
          </div>
          <div className="action-buttons">
            <button 
              className="forward-btn cancel-btn" 
              onClick={onClose}
              disabled={forwarding}
            >
              Cancel
            </button>
            <button 
              className={`forward-btn submit-btn ${selectedTargets.length > 0 ? 'enabled' : ''}`}
              onClick={handleForward}
              disabled={forwarding || selectedTargets.length === 0}
            >
              {forwarding ? (
                <>
                  <span className="loading-spinner">⏳</span>
                  Forwarding...
                </>
              ) : selectedTargets.length > 0 ? (
                <>
                  <span className="forward-icon">📨</span>
                  Send Message
                </>
              ) : (
                'Select destinations first'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForwardMessage;
